import loadable from '@loadable/component';
import { compose } from 'redux';
import type { ComponentType, ElementType } from 'react';
import Loading from '../../../common/loading/components/Loading';

export const loadWithTimeout =
  (ms: number) =>
  <T,>(promise: Promise<T>): Promise<T> => {
    let timeout: ReturnType<typeof setTimeout>;
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeout = setTimeout(
          () => reject(new Error(`Failed to load chunked component due to timeout`)),
          ms,
        );
      }),
    ]).then(
      (component) => {
        clearTimeout(timeout);
        return component;
      },
      (err) => {
        clearTimeout(timeout);
        throw err;
      },
    );
  };

const delayLoading =
  (ms: number) =>
  async <T,>(promise: Promise<T>): Promise<T> => {
    await new Promise((res) => setTimeout(res, ms));
    return promise;
  };

export const DELAY = 200;
export const TIMEOUT = 60 * 1000;

export const createLoadableComponent = <P extends object>(
  loader: () => Promise<{ default: ComponentType<P> }>,
  { FallbackComponent = Loading }: { FallbackComponent?: ElementType } = {},
) => {
  return loadable(
    () => {
      return compose(loadWithTimeout(TIMEOUT), delayLoading(DELAY))(loader());
    },
    {
      fallback: <FallbackComponent />,
    },
  );
};
